import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import Header from '../components/layout/Header';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  Target, 
  Plus, 
  MoreVertical, 
  Edit2, 
  Trash2,
  Flag,
  ChevronDown,
  ChevronRight,
  Mountain,
  Star,
  Compass,
  Heart,
  Building2
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Predefined colors for pillars
const PILLAR_COLORS = [
  { value: '#2563EB', label: 'Blue' },
  { value: '#059669', label: 'Green' },
  { value: '#7C3AED', label: 'Purple' },
  { value: '#DC2626', label: 'Red' },
  { value: '#D97706', label: 'Orange' },
  { value: '#0891B2', label: 'Cyan' },
  { value: '#DB2777', label: 'Pink' },
  { value: '#4B5563', label: 'Gray' },
];

// Foundation element configuration
const FOUNDATION_CONFIG = {
  pinnacle: { 
    label: 'Pinnacle (BHAG)', 
    description: 'Your Big Hairy Audacious Goal - the ultimate destination',
    icon: Star,
    color: 'text-amber-500',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200'
  },
  mission: { 
    label: 'Mission Statement', 
    description: 'What you do and who you serve',
    icon: Compass,
    color: 'text-blue-500',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200'
  },
  purpose: { 
    label: 'Purpose Statement', 
    description: 'Why your organization exists',
    icon: Heart,
    color: 'text-rose-500',
    bgColor: 'bg-rose-50',
    borderColor: 'border-rose-200'
  },
  core_business: { 
    label: 'Core Business', 
    description: 'Your primary business focus and value proposition',
    icon: Building2,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200'
  }
};

const SVEPManagement = () => {
  const { user, isAdmin, isELT } = useAuth();
  const [loading, setLoading] = useState(true);
  
  // Foundation state
  const [foundations, setFoundations] = useState([]);
  const [foundationDialogOpen, setFoundationDialogOpen] = useState(false);
  const [editingFoundation, setEditingFoundation] = useState(null);
  const [foundationForm, setFoundationForm] = useState({ title: '', description: '' });
  
  // Data state
  const [pillars, setPillars] = useState([]);
  const [goals, setGoals] = useState([]);
  const [expandedPillars, setExpandedPillars] = useState({});
  
  // Year filter for goals
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const years = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];
  
  // Dialog state
  const [pillarDialogOpen, setPillarDialogOpen] = useState(false);
  const [goalDialogOpen, setGoalDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Form state
  const [editingPillar, setEditingPillar] = useState(null);
  const [editingGoal, setEditingGoal] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  
  // Pillar form
  const [pillarForm, setPillarForm] = useState({
    name: '',
    description: '',
    color: '#2563EB'
  });
  
  // Goal form
  const [goalForm, setGoalForm] = useState({
    title: '',
    description: '',
    pillar_id: '',
    year: currentYear,
    target_metric: '',
    target_value: '',
    current_value: ''
  });
  
  const [saving, setSaving] = useState(false);

  // Fetch data
  const fetchFoundations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/strategy/foundation`);
      setFoundations(response.data);
    } catch (error) {
      console.error('Failed to fetch foundations:', error);
    }
  }, []);

  const fetchPillars = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/strategy/pillars`);
      setPillars(response.data);
    } catch (error) {
      console.error('Failed to fetch pillars:', error);
      toast.error('Failed to load pillars');
    }
  }, []);

  const fetchGoals = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/strategy/goals`, {
        params: { year: selectedYear }
      });
      setGoals(response.data);
    } catch (error) {
      console.error('Failed to fetch goals:', error);
      toast.error('Failed to load goals');
    }
  }, [selectedYear]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchFoundations(), fetchPillars(), fetchGoals()]);
      setLoading(false);
    };
    loadData();
  }, [fetchFoundations, fetchPillars, fetchGoals]);

  // Toggle pillar expansion
  const togglePillar = (pillarId) => {
    setExpandedPillars(prev => ({
      ...prev,
      [pillarId]: !prev[pillarId]
    }));
  };

  // Get goals for a specific pillar
  const getGoalsForPillar = (pillarId) => {
    return goals.filter(g => g.pillar_id === pillarId);
  };

  // Get foundation by type
  const getFoundation = (type) => {
    return foundations.find(f => f.type === type) || { type, title: null, description: null };
  };

  // === FOUNDATION OPERATIONS ===
  const openFoundationDialog = (type) => {
    const foundation = getFoundation(type);
    setEditingFoundation(type);
    setFoundationForm({
      title: foundation.title || '',
      description: foundation.description || ''
    });
    setFoundationDialogOpen(true);
  };

  const saveFoundation = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/strategy/foundation/${editingFoundation}`, foundationForm);
      toast.success(`${FOUNDATION_CONFIG[editingFoundation].label} updated`);
      setFoundationDialogOpen(false);
      fetchFoundations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // === PILLAR OPERATIONS ===
  const openPillarDialog = (pillar = null) => {
    if (pillar) {
      setEditingPillar(pillar);
      setPillarForm({
        name: pillar.name,
        description: pillar.description || '',
        color: pillar.color || '#2563EB'
      });
    } else {
      setEditingPillar(null);
      setPillarForm({ name: '', description: '', color: '#2563EB' });
    }
    setPillarDialogOpen(true);
  };

  const savePillar = async () => {
    if (!pillarForm.name.trim()) {
      toast.error('Pillar name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingPillar) {
        await axios.put(`${API}/strategy/pillars/${editingPillar.id}`, pillarForm);
        toast.success('Pillar updated');
      } else {
        await axios.post(`${API}/strategy/pillars`, pillarForm);
        toast.success('Pillar created');
      }
      setPillarDialogOpen(false);
      fetchPillars();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save pillar');
    } finally {
      setSaving(false);
    }
  };

  // === GOAL OPERATIONS ===
  const openGoalDialog = (goal = null, defaultPillarId = '') => {
    if (goal) {
      setEditingGoal(goal);
      setGoalForm({
        title: goal.title,
        description: goal.description || '',
        pillar_id: goal.pillar_id,
        year: goal.year,
        target_metric: goal.target_metric || '',
        target_value: goal.target_value?.toString() || '',
        current_value: goal.current_value?.toString() || ''
      });
    } else {
      setEditingGoal(null);
      setGoalForm({
        title: '',
        description: '',
        pillar_id: defaultPillarId || (pillars[0]?.id || ''),
        year: selectedYear,
        target_metric: '',
        target_value: '',
        current_value: ''
      });
    }
    setGoalDialogOpen(true);
  };

  const saveGoal = async () => {
    if (!goalForm.title.trim()) {
      toast.error('Goal title is required');
      return;
    }
    if (!goalForm.pillar_id) {
      toast.error('Please select a pillar');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...goalForm,
        year: parseInt(goalForm.year),
        target_value: goalForm.target_value ? parseFloat(goalForm.target_value) : null,
        current_value: goalForm.current_value ? parseFloat(goalForm.current_value) : null
      };

      if (editingGoal) {
        await axios.put(`${API}/strategy/goals/${editingGoal.id}`, payload);
        toast.success('Goal updated');
      } else {
        await axios.post(`${API}/strategy/goals`, payload);
        toast.success('Goal created');
      }
      setGoalDialogOpen(false);
      fetchGoals();
      fetchPillars(); // Refresh pillar counts
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save goal');
    } finally {
      setSaving(false);
    }
  };

  // === DELETE OPERATIONS ===
  const openDeleteDialog = (type, item) => {
    setItemToDelete({ type, item });
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    setSaving(true);
    try {
      const { type, item } = itemToDelete;
      if (type === 'pillar') {
        await axios.delete(`${API}/strategy/pillars/${item.id}`);
        toast.success('Pillar deleted');
        fetchPillars();
      } else if (type === 'goal') {
        await axios.delete(`${API}/strategy/goals/${item.id}`);
        toast.success('Goal deleted');
        fetchGoals();
        fetchPillars();
      }
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    } finally {
      setSaving(false);
    }
  };

  const canManage = isAdmin() || isELT();

  if (loading) {
    return (
      <Layout>
        <Header title="SVEP Management" />
        <div className="p-4 sm:p-6 space-y-6">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Header title="SVEP Management" />
      
      <div className="p-4 sm:p-6 space-y-8" data-testid="svep-management">
        {/* Foundation Section */}
        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Strategic Foundation</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(FOUNDATION_CONFIG).map(([type, config]) => {
              const foundation = getFoundation(type);
              const IconComponent = config.icon;
              const hasContent = foundation.title || foundation.description;
              
              return (
                <Card 
                  key={type} 
                  className={`relative overflow-hidden border ${config.borderColor} ${config.bgColor}`}
                  data-testid={`foundation-${type}`}
                >
                  <CardContent className="pt-5 pb-4">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg bg-white shadow-sm ${config.color}`}>
                        <IconComponent className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-semibold text-slate-900 text-sm">{config.label}</h3>
                          {canManage && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-7 px-2 text-slate-500 hover:text-slate-900"
                              onClick={() => openFoundationDialog(type)}
                              data-testid={`edit-foundation-${type}`}
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                        
                        {hasContent ? (
                          <>
                            {foundation.title && (
                              <p className="font-medium text-slate-800 mb-1">{foundation.title}</p>
                            )}
                            {foundation.description && (
                              <p className="text-sm text-slate-600 line-clamp-3">{foundation.description}</p>
                            )}
                            {foundation.updated_by_name && (
                              <p className="text-xs text-slate-400 mt-2">
                                Last updated by {foundation.updated_by_name}
                              </p>
                            )}
                          </>
                        ) : (
                          <p className="text-sm text-slate-400 italic">
                            {canManage ? 'Click edit to define...' : 'Not yet defined'}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-slate-200" />

        {/* Strategic Pillars Section */}
        <div>
          <div className="flex flex-wrap gap-4 items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900">Strategic Pillars</h2>
            <div className="flex items-center gap-3">
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger className="w-28" data-testid="year-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map(y => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {canManage && (
                <Button onClick={() => openPillarDialog()} data-testid="add-pillar-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Pillar
                </Button>
              )}
            </div>
          </div>

        {/* Pillars List */}
        {pillars.length === 0 ? (
          <Card className="p-8 text-center">
            <Target className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 mb-4">No strategic pillars defined yet</p>
            {canManage && (
              <Button onClick={() => openPillarDialog()}>
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Pillar
              </Button>
            )}
          </Card>
        ) : (
          <div className="space-y-4">
            {pillars.map(pillar => {
              const pillarGoals = getGoalsForPillar(pillar.id);
              const isExpanded = expandedPillars[pillar.id];
              
              return (
                <Card key={pillar.id} className="overflow-hidden">
                  <div 
                    className="flex items-center gap-4 p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => togglePillar(pillar.id)}
                    data-testid={`pillar-${pillar.id}`}
                  >
                    <div 
                      className="w-1.5 h-12 rounded-full" 
                      style={{ backgroundColor: pillar.color }}
                    />
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900">{pillar.name}</h3>
                        <span className="px-2 py-0.5 text-xs bg-slate-100 text-slate-600 rounded">
                          {pillar.goals_count} goals
                        </span>
                        <span className="px-2 py-0.5 text-xs bg-blue-50 text-blue-600 rounded">
                          {pillar.rocks_count} rocks
                        </span>
                      </div>
                      {pillar.description && (
                        <p className="text-sm text-slate-500 truncate mt-1">{pillar.description}</p>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      {canManage && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openPillarDialog(pillar); }}>
                              <Edit2 className="w-4 h-4 mr-2" />
                              Edit Pillar
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openGoalDialog(null, pillar.id); }}>
                              <Flag className="w-4 h-4 mr-2" />
                              Add Goal
                            </DropdownMenuItem>
                            {isAdmin() && (
                              <DropdownMenuItem 
                                className="text-red-600"
                                onClick={(e) => { e.stopPropagation(); openDeleteDialog('pillar', pillar); }}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete Pillar
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                      
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Goals Section */}
                  {isExpanded && (
                    <div className="border-t bg-slate-50 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-slate-700">
                          Annual Goals ({selectedYear})
                        </h4>
                        {canManage && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => openGoalDialog(null, pillar.id)}
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Add Goal
                          </Button>
                        )}
                      </div>
                      
                      {pillarGoals.length === 0 ? (
                        <p className="text-sm text-slate-400 text-center py-4">
                          No goals defined for {selectedYear}
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {pillarGoals.map(goal => (
                            <div 
                              key={goal.id}
                              className="bg-white rounded-lg border p-3 flex items-start justify-between"
                            >
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <Flag className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                  <span className="font-medium text-slate-900">{goal.title}</span>
                                  {goal.rocks_count > 0 && (
                                    <Link 
                                      to={`/strategy/rocks?goal=${goal.id}`}
                                      className="text-xs text-blue-600 hover:underline"
                                    >
                                      <Mountain className="w-3 h-3 inline mr-1" />
                                      {goal.rocks_count} rocks
                                    </Link>
                                  )}
                                </div>
                                {goal.description && (
                                  <p className="text-sm text-slate-500 mt-1 ml-6">{goal.description}</p>
                                )}
                                {goal.target_metric && (
                                  <div className="flex items-center gap-4 mt-2 ml-6 text-sm">
                                    <span className="text-slate-500">{goal.target_metric}:</span>
                                    {goal.progress_percentage !== null && (
                                      <div className="flex items-center gap-2">
                                        <div className="w-24 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                          <div 
                                            className="h-full bg-blue-500 rounded-full"
                                            style={{ width: `${Math.min(100, goal.progress_percentage)}%` }}
                                          />
                                        </div>
                                        <span className="text-slate-600">
                                          {goal.current_value?.toLocaleString()} / {goal.target_value?.toLocaleString()}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                              
                              {canManage && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                                      <MoreVertical className="w-4 h-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => openGoalDialog(goal)}>
                                      <Edit2 className="w-4 h-4 mr-2" />
                                      Edit Goal
                                    </DropdownMenuItem>
                                    <Link to={`/strategy/rocks?goal=${goal.id}&pillar=${pillar.id}`}>
                                      <DropdownMenuItem>
                                        <Mountain className="w-4 h-4 mr-2" />
                                        View Rocks
                                      </DropdownMenuItem>
                                    </Link>
                                    {isAdmin() && (
                                      <DropdownMenuItem 
                                        className="text-red-600"
                                        onClick={() => openDeleteDialog('goal', goal)}
                                      >
                                        <Trash2 className="w-4 h-4 mr-2" />
                                        Delete Goal
                                      </DropdownMenuItem>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
        </div>
      </div>

      {/* Foundation Dialog */}
      <Dialog open={foundationDialogOpen} onOpenChange={setFoundationDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingFoundation && FOUNDATION_CONFIG[editingFoundation]?.label}
            </DialogTitle>
            <DialogDescription>
              {editingFoundation && FOUNDATION_CONFIG[editingFoundation]?.description}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="foundation-title">Title</Label>
              <Input
                id="foundation-title"
                value={foundationForm.title}
                onChange={(e) => setFoundationForm({ ...foundationForm, title: e.target.value })}
                placeholder="Enter a concise title..."
                data-testid="foundation-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="foundation-description">Description</Label>
              <Textarea
                id="foundation-description"
                value={foundationForm.description}
                onChange={(e) => setFoundationForm({ ...foundationForm, description: e.target.value })}
                placeholder="Provide more detail..."
                rows={4}
                data-testid="foundation-description-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setFoundationDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveFoundation} disabled={saving} data-testid="save-foundation-btn">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pillar Dialog */}
      <Dialog open={pillarDialogOpen} onOpenChange={setPillarDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingPillar ? 'Edit Pillar' : 'Create Strategic Pillar'}</DialogTitle>
            <DialogDescription>
              Pillars represent the core strategic focus areas for your organization.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="pillar-name">Name *</Label>
              <Input
                id="pillar-name"
                value={pillarForm.name}
                onChange={(e) => setPillarForm({ ...pillarForm, name: e.target.value })}
                placeholder="e.g., Customer Growth"
                data-testid="pillar-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="pillar-description">Description</Label>
              <Textarea
                id="pillar-description"
                value={pillarForm.description}
                onChange={(e) => setPillarForm({ ...pillarForm, description: e.target.value })}
                placeholder="Describe this strategic pillar..."
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2">
                {PILLAR_COLORS.map(color => (
                  <button
                    key={color.value}
                    type="button"
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      pillarForm.color === color.value 
                        ? 'border-slate-900 scale-110' 
                        : 'border-transparent hover:scale-105'
                    }`}
                    style={{ backgroundColor: color.value }}
                    onClick={() => setPillarForm({ ...pillarForm, color: color.value })}
                    title={color.label}
                  />
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPillarDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={savePillar} disabled={saving} data-testid="save-pillar-btn">
              {saving ? 'Saving...' : (editingPillar ? 'Update Pillar' : 'Create Pillar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Goal Dialog */}
      <Dialog open={goalDialogOpen} onOpenChange={setGoalDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingGoal ? 'Edit Goal' : 'Create Annual Goal'}</DialogTitle>
            <DialogDescription>
              Annual goals define specific targets under each strategic pillar.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="goal-title">Title *</Label>
              <Input
                id="goal-title"
                value={goalForm.title}
                onChange={(e) => setGoalForm({ ...goalForm, title: e.target.value })}
                placeholder="e.g., Increase Premium to $50M"
                data-testid="goal-title-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="goal-pillar">Pillar *</Label>
                <Select 
                  value={goalForm.pillar_id} 
                  onValueChange={(v) => setGoalForm({ ...goalForm, pillar_id: v })}
                >
                  <SelectTrigger data-testid="goal-pillar-select">
                    <SelectValue placeholder="Select pillar" />
                  </SelectTrigger>
                  <SelectContent>
                    {pillars.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-2 h-2 rounded-full" 
                            style={{ backgroundColor: p.color }} 
                          />
                          {p.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="goal-year">Year *</Label>
                <Select 
                  value={goalForm.year.toString()} 
                  onValueChange={(v) => setGoalForm({ ...goalForm, year: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="goal-description">Description</Label>
              <Textarea
                id="goal-description"
                value={goalForm.description}
                onChange={(e) => setGoalForm({ ...goalForm, description: e.target.value })}
                placeholder="Describe this goal..."
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="goal-metric">Target Metric (Optional)</Label>
              <Input
                id="goal-metric"
                value={goalForm.target_metric}
                onChange={(e) => setGoalForm({ ...goalForm, target_metric: e.target.value })}
                placeholder="e.g., Revenue, Users, Premium"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="goal-target">Target Value</Label>
                <Input
                  id="goal-target"
                  type="number"
                  value={goalForm.target_value}
                  onChange={(e) => setGoalForm({ ...goalForm, target_value: e.target.value })}
                  placeholder="e.g., 50000000"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="goal-current">Current Value</Label>
                <Input
                  id="goal-current"
                  type="number"
                  value={goalForm.current_value}
                  onChange={(e) => setGoalForm({ ...goalForm, current_value: e.target.value })}
                  placeholder="e.g., 35000000"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setGoalDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveGoal} disabled={saving} data-testid="save-goal-btn">
              {saving ? 'Saving...' : (editingGoal ? 'Update Goal' : 'Create Goal')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-red-600">
              Delete {itemToDelete?.type === 'pillar' ? 'Pillar' : 'Goal'}
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{itemToDelete?.item?.name || itemToDelete?.item?.title}"?
              {itemToDelete?.type === 'pillar' && (
                <span className="block mt-2 text-amber-600">
                  This will also affect any rocks linked to this pillar.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDelete}
              disabled={saving}
              data-testid="confirm-delete-btn"
            >
              {saving ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default SVEPManagement;
